import type { JSX, ReactNode } from 'react';
import { useTranslation } from '../i18n';

export interface PanelProps {
  title: string;
  /** Техническая приписка рядом с заголовком: количество, площадка. */
  subtitle?: ReactNode;
  /** Действия справа в шапке панели. */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Панель по макету: шапка 44px, рамка, скруглённые углы.
 *
 * На телефоне шапка перестаёт быть строкой. Заголовок, счётчик и действия
 * не помещаются в 360 px никогда: один только поиск в журнале занимает
 * 220 px, а рядом с ним стоит список действий. Поэтому у́же планшета
 * действия переносятся на свою строку во всю ширину, а высота шапки
 * перестаёт быть заданной. Начиная с `sm` (640 px — та же граница, что
 * и у `viewport`) всё как было.
 */
export function Panel({ title, subtitle, actions, children }: PanelProps): JSX.Element {
  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-line bg-surface">
      <header className="flex flex-none flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-line bg-raised px-3.5 py-2 sm:h-11 sm:flex-nowrap sm:py-0">
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle ? <div className="font-mono text-xs text-dim">{subtitle}</div> : null}
        {actions ? (
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto sm:flex-nowrap">
            {actions}
          </div>
        ) : null}
      </header>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  );
}

/**
 * Плотная шапка экрана, которая заодно панель управления, — видеостена
 * и видеоархив (разделы 07 и 09 макета).
 *
 * Отличается от шапки `Panel` не прихотью, а тем, что на этих экранах
 * съедает заголовок: место занимает картинка, и каждый лишний пиксель
 * шапки отнимается у неё. Поэтому 38 px против 44 и заголовок в 13 px
 * против 18. Постоянного набора действий у таких экранов нет — сетки
 * и раскладки у стены, сутки и провайдер у архива, — и они приходят
 * потомками, а не отдельным `actions`.
 *
 * Заведена после того, как обе шапки разошлись: одна была высотой 44 px
 * без подложки с заголовком в 14 px, другая — 38 px с подложкой
 * и заголовком в 13 px. Разница видна, если открыть экраны подряд,
 * а объяснить её нечем.
 *
 * Перенос вместо обрезки: на 360 px строка не вмещает даже сокращённый
 * набор кнопок, и вторая строка лучше, чем кнопка за краем экрана.
 * На широком экране всё по-прежнему в одну строку и той же высоты.
 */
export function ToolbarHeader({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <header className="flex min-h-[38px] flex-none flex-wrap items-center gap-2 gap-y-1 border-b border-line bg-raised px-3 py-1">
      <h2 className="flex-none text-[13px] font-semibold">{title}</h2>
      {children}
    </header>
  );
}

/**
 * Пустое состояние. §12 требует не «нет данных», а приглашения к действию.
 */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-2 px-4 py-8">
      <div className="text-base font-medium">{title}</div>
      <div className="max-w-[520px] text-sm leading-[1.5] text-muted">{hint}</div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}

/** Сообщение об отказе или сбое — с указанием, что делать. */
export function ErrorState({ title, hint }: { title: string; hint: string }): JSX.Element {
  return (
    <div className="m-4 flex flex-col gap-1.5 rounded-xs border-l-2 border-l-down bg-overlay px-3 py-2.5">
      <div className="text-base font-medium">{title}</div>
      <div className="text-sm leading-[1.45] text-muted">{hint}</div>
    </div>
  );
}

/**
 * Ожидание загрузки.
 *
 * Подпись по умолчанию берётся из словаря, а не из значения параметра:
 * значение вычисляется один раз при загрузке модуля, а язык переключают
 * на живой странице — подпись осталась бы прежней до перезагрузки.
 */
export function Loading({ text }: { text?: string }): JSX.Element {
  const { t } = useTranslation();

  return <div className="px-4 py-6 text-sm text-muted">{text ?? t('common.loading')}</div>;
}
